import React, { useCallback, useEffect, useRef, useState } from "react";
import { GlobalButton } from "../../../../global/forms/GlobalFormFields";
import { GlobalModal } from "../../../../global/GlobalModal";
import { useEyeBlinkLiveness } from "../../../../global/hooks/useEyeBlinkLiveness";

export default function MarkAttendanceCameraModal({ open, onClose, onConfirm, busy, submitError }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState("");

  const blinkLiveness = useEyeBlinkLiveness({
    active: open && !preview && !error,
    videoRef,
  });

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      stopCamera();
      setPreview("");
      setError("");
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch {
        if (!cancelled) setError("Camera access denied or unavailable.");
      }
    })();

    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, stopCamera]);

  const handleCaptureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    setPreview(canvas.toDataURL("image/jpeg", 0.9));
    stopCamera();
  };

  const handleUseCapturedPhoto = async () => {
    if (!preview) return;
    onConfirm?.(preview);
  };

  const displayError = submitError || error;

  return (
    <GlobalModal
      open={open}
      title="Capture your face"
      onClose={() => !busy && onClose?.()}
      footer={
        <>
          <GlobalButton type="button" variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </GlobalButton>
          {preview ? (
            <GlobalButton type="button" variant="primary" onClick={handleUseCapturedPhoto} disabled={busy}>
              {busy ? "Saving…" : "OK — mark attendance"}
            </GlobalButton>
          ) : (
            <GlobalButton
              type="button"
              variant="primary"
              onClick={handleCaptureFrame}
              disabled={Boolean(error) || !blinkLiveness.canCapture}
            >
              Capture
            </GlobalButton>
          )}
        </>
      }
    >
      <div className="att-mark-camera-stage">
        {!preview ? (
          <>
            <video ref={videoRef} className="att-mark-camera" playsInline muted aria-label="Camera preview" />
            {!error ? (
              <p
                className={`att-mark-camera-blink-status${
                  blinkLiveness.blinkVerified ? " att-mark-camera-blink-status--ok" : ""
                }`}
                aria-live="polite"
              >
                {blinkLiveness.statusText}
              </p>
            ) : null}
          </>
        ) : (
          <img src={preview} alt="Captured preview" className="att-mark-camera att-mark-camera--preview" />
        )}
        {displayError ? (
          <div key={displayError} className="att-mark-camera-error" role="alert" aria-live="assertive">
            <p className="att-mark-camera-error__text">{displayError}</p>
            <div className="att-mark-camera-error__icon" aria-hidden>
              <span className="att-mark-camera-error__ring" />
              <span className="att-mark-camera-error__mark">!</span>
            </div>
          </div>
        ) : null}
      </div>
      <canvas ref={canvasRef} hidden />
      <p className="att-mark-camera__hint">
        Blink once when prompted, then capture. Both your profile photo and this capture are sent for
        server verification only — nothing is stored.
      </p>
    </GlobalModal>
  );
}
